import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { sendTestFlags, submitTest } from "../../api/utils";

const TestPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { test_data, test_id } = location.state || {};

  const [questions, setQuestions] = useState([]);
  const [duration, setDuration] = useState(60); // in minutes
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [message, setMessage] = useState(""); // non-blocking messages

  const timerRef = useRef(null);
  const submittedRef = useRef(false);
  const ignoreBlurRef = useRef(false);
  const ignoreFullscreenRef = useRef(false);

  // Load test data
  useEffect(() => {
    if (test_data) {
      setQuestions(test_data.questions || []);
      setDuration(test_data.duration || 60);
      setTimeLeft((test_data.duration || 60) * 60);
      sessionStorage.setItem("test_taken", "started");
    }
  }, [test_data]);

  // Handle answer selection
  const handleAnswer = (optionId) => {
    const questionId = questions[currentQ].id;
    setAnswers((prev) => {
      const existingIndex = prev.findIndex((a) => a.question_id === questionId);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex].selected_option = optionId;
        return updated;
      } else {
        return [
          ...prev,
          { question_id: questionId, selected_option: optionId },
        ];
      }
    });
  };

  // Perform submit
  const performSubmit = async () => {
    ignoreBlurRef.current = true; // prevent tab switch handling during submit
    const allAttempted = questions.length === answers.length;

    if (!allAttempted && !confirmSubmit) {
      setMessage(
        "You have not attempted all questions. Click submit again if you still want to submit."
      );
      setConfirmSubmit(true);
      return;
    }

    if (submittedRef.current) return;
    submittedRef.current = true;

    const data = { test_id, answers };
    setMessage("Submitting test...");

    try {
      const response = await submitTest(data);
      if (response.status) {
        sessionStorage.setItem("test_taken", "finished");

        // Ignore the next fullscreen exit event
        ignoreFullscreenRef.current = true;

        // Exit fullscreen programmatically
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }

        navigate("/examination/landing");
      } else {
        setMessage("Failed to submit test. Try again.");
      }
    } catch (error) {
      console.error(error);
      setMessage("Error submitting test.");
    }
  };

  // Timer
  useEffect(() => {
    if (timeLeft === null) return;

    if (timeLeft <= 0) {
      performSubmit();
      return;
    }

    timerRef.current = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);

    return () => clearTimeout(timerRef.current);
  }, [timeLeft]);

  // Fullscreen exit detection
  useEffect(() => {
    const handleFullscreenExit = async () => {
      if (submittedRef.current) return;

      if (!document.fullscreenElement) {
        if (ignoreFullscreenRef.current) {
          // Reset the flag and ignore this event
          ignoreFullscreenRef.current = false;
          return;
        }

        setMessage("You exited fullscreen. Test terminated.");
        try {
          let data = { test_id, flag: "Exit Fullscreen" };
          await sendTestFlags(data);
          sessionStorage.setItem("test_taken", "finished");
        } catch (error) {
          console.error(error);
        }
        navigate("/examination/landing");
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenExit);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenExit);
  }, [navigate, test_id]);

  // Tab switch detection
  useEffect(() => {
    const handleTabSwitch = async () => {
      if (submittedRef.current || ignoreBlurRef.current) return;

      alert("Tab switching is not allowed. Test ended.");
      try {
        let data = { test_id, flag: "Tab Switch" };
        await sendTestFlags(data);
        sessionStorage.setItem("test_taken", "finished");
      } catch (error) {
        console.error(error);
      }
      navigate("/examination/landing");
    };

    window.addEventListener("blur", handleTabSwitch);
    return () => window.removeEventListener("blur", handleTabSwitch);
  }, [navigate, test_id]);

  if (!questions.length || timeLeft === null) return <h2>Loading test...</h2>;

  const q = questions[currentQ];

  const formatTime = (t) => {
    const m = Math.floor(t / 60);
    const s = t % 60;
    return `${m}:${s < 10 ? "0" + s : s}`;
  };

  const renderQuestionContent = (question) => (
    <div>
      {question.type === "text" && (
        <p style={{ fontSize: "1.4rem" }}>{question.question}</p>
      )}
      {question.type === "image" && (
        <div>
          {question.question_img && (
            <img
              src={question.question_img}
              alt="Question"
              style={{ maxWidth: "100%", marginBottom: "1rem" }}
            />
          )}
          <p style={{ fontSize: "1.4rem" }}>{question.question}</p>
        </div>
      )}
    </div>
  );

  const renderOption = (option, questionId) => (
    <label
      key={`${questionId}-${option.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px",
        borderBottom: "1px solid #eee",
      }}
    >
      <input
        type="radio"
        name={`q-${questionId}`}
        checked={
          answers.find((a) => a.question_id === questionId)?.selected_option ===
          option.id
        }
        onChange={() => handleAnswer(option.id)}
        style={{ marginRight: "10px" }}
      />

      {option.type === "text" && (
        <span style={{ flex: 1, marginLeft: "10px" }}>{option.text}</span>
      )}
      {option.type === "image" && option.image_url && (
        <img
          src={option.image_url}
          alt={`Option ${option.id}`}
          style={{ maxWidth: "150px", marginRight: "auto" }}
        />
      )}
    </label>
  );

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw" }}>
      {/* Sidebar */}
      <div style={{ width: "20%", padding: "1rem", overflowY: "auto" }}>
        <h3>Questions</h3>
        {questions.map((item, index) => {
          const attempted = answers.some((a) => a.question_id === item.id);
          return (
            <button
              key={item.id}
              onClick={() => setCurrentQ(index)}
              style={{
                display: "block",
                width: "100%",
                padding: "10px",
                marginBottom: "8px",
                background:
                  currentQ === index
                    ? "#0A3980"
                    : attempted
                    ? "#28a745"
                    : "#ddd",
                color: currentQ === index || attempted ? "white" : "black",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Question {index + 1}
            </button>
          );
        })}
      </div>

      {/* Main Question Area */}
      <div style={{ flex: 1, padding: "2rem" }}>
        <h2>Question {currentQ + 1}</h2>
        {renderQuestionContent(q)}

        <div style={{ width: "60%" }}>
          {q.options.map((opt) => renderOption(opt, q.id))}
        </div>

        {/* Navigation Buttons */}
        <div
          style={{
            marginTop: "2rem",
            display: "flex",
            justifyContent: "space-between",
            width: "60%",
          }}
        >
          <button
            onClick={() => setCurrentQ((prev) => Math.max(prev - 1, 0))}
            disabled={currentQ === 0}
            style={{
              padding: "10px 20px",
              background: currentQ === 0 ? "#ccc" : "#0A3980",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: currentQ === 0 ? "not-allowed" : "pointer",
              fontSize: "1rem",
            }}
          >
            Previous
          </button>

          <button
            onClick={() =>
              setCurrentQ((prev) => Math.min(prev + 1, questions.length - 1))
            }
            disabled={currentQ === questions.length - 1}
            style={{
              padding: "10px 20px",
              background:
                currentQ === questions.length - 1 ? "#ccc" : "#0A3980",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor:
                currentQ === questions.length - 1 ? "not-allowed" : "pointer",
              fontSize: "1rem",
            }}
          >
            Next
          </button>
        </div>
      </div>

      {/* Timer + Submit */}
      <div
        style={{
          width: "20%",
          padding: "1rem",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <h3>Time Left</h3>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            marginTop: "2rem",
            color: timeLeft < 10 ? "red" : "black",
          }}
        >
          {formatTime(timeLeft)}
        </div>
        {message && (
          <div
            style={{
              background: "red",
              padding: "10px 15px",
              marginBottom: "1rem",
              borderRadius: "5px",
              color: "#fff",
            }}
          >
            {message}
          </div>
        )}

        <button
          onClick={performSubmit}
          style={{
            marginTop: "2rem",
            padding: "0.5rem 1rem",
            background: "#28a745",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "1.2rem",
            width: "100%",
          }}
        >
          Submit Test
        </button>
      </div>
    </div>
  );
};

export default TestPage;
